import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Loader2, Save, Trash2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHtmlChatPanel } from "@/routes/pages/edit/PageHtmlChatPanel";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import type { AutomationStep, AutomationTrigger } from "@/types/Automation";
import type {
  PageCheckoutLineItem,
  PageCheckoutSettings,
  PageOnCheckoutSuccessAction,
} from "@/types/pageFormSubmit";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  DEFAULT_PUBLIC_REPLIES,
  STEP_OPTIONS_MAIN,
  STEP_OPTIONS_MAILERLITE,
  STEP_OPTIONS_LAYLO,
  STEP_OPTIONS_MANYCHAT,
  STEP_OPTIONS_BEEHIIV,
  STEP_OPTIONS_META,
} from "./constants";
import { edgeTypes, nodeTypes } from "./reactFlowTypes";
import { TemplateTab } from "./TemplateTab";
import type { Automation, WaitMode } from "./types";
import {
  formatDateInTimeZoneForInput,
  generateStepId,
  getValidTimeZone,
  zonedInputToUtcIso,
} from "./utils";

function getFlowKey({
  name,
  trigger,
  steps,
}: {
  name: string;
  trigger: AutomationTrigger | null;
  steps: AutomationStep[];
}) {
  return JSON.stringify({
    name,
    trigger: trigger ? { ...trigger, position: undefined } : null,
    steps: steps.map((s) => {
      const { position: _position, ...rest } = s;
      return rest;
    }),
  });
}

export function AutomationEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [automation, setAutomation] = useState<Automation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState<string>("");
  const nameRef = useRef<HTMLDivElement>(null);
  const [trigger, setTrigger] = useState<AutomationTrigger | null>(null);
  const [steps, setSteps] = useState<AutomationStep[]>([]);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedFlowKey, setSavedFlowKey] = useState("");
  const [positionsDirty, setPositionsDirty] = useState(false);
  const [isStepDialogOpen, setIsStepDialogOpen] = useState(false);
  const [emailBodyEditStepId, setEmailBodyEditStepId] = useState<string | null>(null);
  const [emailBodyEditValue, setEmailBodyEditValue] = useState("");
  const [hasTwilio, setHasTwilio] = useState<boolean | null>(null);
  const [hasMetaAccount, setHasMetaAccount] = useState<boolean | null>(null);
  const [hasMetaFacebook, setHasMetaFacebook] = useState<boolean | null>(null);
  const [hasMailerLite, setHasMailerLite] = useState<boolean | null>(null);
  const [hasLaylo, setHasLaylo] = useState<boolean | null>(null);
  const [hasManychat, setHasManychat] = useState<boolean | null>(null);
  const [hasBeehiiv, setHasBeehiiv] = useState<boolean | null>(null);
  const [artistTimezone, setArtistTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const applyLoadedAutomation = useCallback((dataAutomation: Automation) => {
    setAutomation(dataAutomation);
    setName(dataAutomation.name || "");
    const nextSteps = (dataAutomation.steps || []).map((step: AutomationStep) => {
      if (step.type !== "wait") return step;
      const tz = getValidTimeZone({ value: (step.data?.timezone as string) || artistTimezone });
      return {
        ...step,
        data: {
          ...step.data,
          mode: (step.data?.mode as WaitMode) || "duration",
          timezone: tz,
          untilAtLocal: formatDateInTimeZoneForInput({
            value: typeof step.data?.untilAt === "string" ? step.data.untilAt : "",
            timeZone: tz,
          }),
        },
      };
    });
    setSteps(nextSteps);
    const t = dataAutomation.triggers?.[0] || null;
    const triggerNext = t?.next ?? (dataAutomation as { firstStepId?: string }).firstStepId ?? nextSteps[0]?.id;
    const nextTrigger = t ? { ...t, next: triggerNext } : null;
    setTrigger(nextTrigger);
    setSavedFlowKey(getFlowKey({ name: dataAutomation.name || "", trigger: nextTrigger, steps: nextSteps }));
    setPositionsDirty(false);
    if (id) {
      setBreadcrumbs([
        { label: "Flows", path: "/automations" },
        { label: dataAutomation.name, path: `/automations/${id}/edit` },
      ]);
    }
  }, [artistTimezone, id, setBreadcrumbs]);

  useEffect(() => {
    Promise.all([
      fetch("/api/twilio").then((res) => res.json()).then((data) => Boolean(data?.twilio)).catch(() => false),
      fetch("/api/meta-account").then((res) => res.json()).then((data) => Boolean(data?.metaAccount)).catch(() => false),
      fetch("/api/ig-account").then((res) => res.json()).then((data) => Boolean(data?.igAccount)).catch(() => false),
      fetch("/api/meta-account?type=facebook").then((res) => res.json()).then((data) => Boolean(data?.metaAccount)).catch(() => false),
      fetch("/api/dashboard")
        .then((res) => res.json())
        .then((data) => (typeof data?.artist?.timezone === "string" ? data.artist.timezone : ""))
        .catch(() => ""),
      fetch("/api/mailerlite")
        .then((res) => res.json())
        .then((data) => Boolean(data?.mailerLite?.apiKey))
        .catch(() => false),
      fetch("/api/laylo")
        .then((res) => res.json())
        .then((data) => Boolean(data?.laylo?.apiKey))
        .catch(() => false),
      fetch("/api/manychat")
        .then((res) => res.json())
        .then((data) => Boolean(data?.manychat?.apiKey))
        .catch(() => false),
      fetch("/api/beehiiv")
        .then((res) => res.json())
        .then((data) => Boolean(data?.beehiiv?.apiKey))
        .catch(() => false),
    ]).then(([twilio, meta, igAccount, metaFacebook, timezone, mailerLite, laylo, manychat, beehiiv]) => {
      setHasTwilio(twilio);
      setHasMetaAccount(meta || igAccount);
      setHasMetaFacebook(metaFacebook);
      setHasMailerLite(mailerLite);
      setHasLaylo(laylo);
      setHasManychat(manychat);
      setHasBeehiiv(beehiiv);
      if (timezone) {
        setArtistTimezone(getValidTimeZone({ value: timezone }));
      }
    });
  }, []);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetch(`/api/automations/${id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.automation) {
            applyLoadedAutomation(data.automation);
          }
        })
        .catch((error) => {
          console.error("Error loading automation:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    return () => {
      clearBreadcrumbs();
    };
  }, [id, applyLoadedAutomation, clearBreadcrumbs]);

  useEffect(() => {
    if (nameRef.current && name !== nameRef.current.textContent) {
      nameRef.current.textContent = name;
    }
  }, [name]);

  const handleNameChange = () => {
    if (nameRef.current) {
      setName(nameRef.current.textContent || "");
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      const stepsWithPosition = steps.map((s) => {
        const position = nodes.find((n) => n.id === s.id)?.position ?? s.position;
        if (s.type !== "wait") {
          return { ...s, position };
        }

        const mode = (s.data?.mode as WaitMode) || "duration";
        const nextData = { ...s.data } as Record<string, unknown>;
        if (mode === "until") {
          nextData.mode = "until";
          const tz = getValidTimeZone({ value: (nextData.timezone as string) || artistTimezone });
          const untilAt = zonedInputToUtcIso({
            value: nextData.untilAtLocal as string,
            timeZone: tz,
          });
          nextData.untilAt = untilAt;
          nextData.timezone = tz;
          delete nextData.duration;
          delete nextData.unit;
        } else {
          nextData.mode = "duration";
          delete nextData.untilAt;
          delete nextData.timezone;
        }
        delete nextData.untilAtLocal;

        return {
          ...s,
          data: nextData,
          position,
        };
      });
      const triggerWithPosition = trigger
        ? {
            ...(trigger.type === "instagram.comment" && !trigger.data
              ? { ...trigger, data: { commentType: "keywords", keywords: [], publicReplies: DEFAULT_PUBLIC_REPLIES } }
              : trigger),
            position: nodes.find((n) => n.id === "trigger")?.position ?? trigger.position,
          }
        : null;
      const response = await fetch(`/api/automations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          steps: stepsWithPosition,
          triggers: triggerWithPosition ? [triggerWithPosition] : [],
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save flow");
      }
      setSavedFlowKey(getFlowKey({ name, trigger, steps }));
      setPositionsDirty(false);
    } catch (error) {
      console.error("Error saving automation:", error);
      alert(error instanceof Error ? error.message : "Failed to save flow");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/automations");
  };

  const handleDelete = async () => {
    if (!id || !confirm("Are you sure you want to delete this flow?")) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/automations/${id}`, { method: "DELETE" });
      if (response.ok) {
        navigate("/automations");
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete flow");
      }
    } catch (error) {
      console.error("Failed to delete automation:", error);
      alert("Failed to delete flow");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTriggerChange = useCallback((t: AutomationTrigger) => {
    setTrigger(t);
  }, []);

  const handleDeleteTrigger = useCallback(() => {
    setTrigger(null);
  }, []);

  const handleStepDataChange = useCallback(({ stepId, field, value }: { stepId: string; field: string; value: string }) => {
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== stepId) return s;
        if (s.type !== "wait") return { ...s, data: { ...s.data, [field]: value } };

        const currentMode = (s.data?.mode as WaitMode) || "duration";
        if (field === "mode") {
          const nextMode = value === "until" ? "until" : "duration";
          if (nextMode === "until") {
            return { ...s, data: { ...s.data, mode: "until", duration: undefined, unit: undefined, timezone: (s.data?.timezone as string) || artistTimezone } };
          }
          return { ...s, data: { ...s.data, mode: "duration", untilAt: undefined, untilAtLocal: undefined, timezone: undefined } };
        }

        if (currentMode === "duration" && (field === "duration" || field === "unit")) {
          return { ...s, data: { ...s.data, mode: "duration", [field]: value } };
        }

        if (currentMode === "until" && (field === "untilAtLocal" || field === "timezone")) {
          return { ...s, data: { ...s.data, mode: "until", [field]: value } };
        }

        return { ...s, data: { ...s.data, [field]: value } };
      })
    );
  }, [artistTimezone]);

  const handleStepButtonsChange = useCallback(
    ({ stepId, buttons }: { stepId: string; buttons: { text: string; targetStepId?: string }[] }) => {
      setSteps((prev) =>
        prev.map((s) => (s.id === stepId ? { ...s, data: { ...s.data, buttons } } : s))
      );
    },
    []
  );

  const handleMailerLiteGroupIdsChange = useCallback(
    ({ stepId, groupIds }: { stepId: string; groupIds: string[] }) => {
      setSteps((prev) =>
        prev.map((s) =>
          s.id === stepId ? { ...s, data: { ...s.data, groupIds } } : s
        )
      );
    },
    []
  );

  const handleManychatTagIdsChange = useCallback(
    ({ stepId, tagIds }: { stepId: string; tagIds: string[] }) => {
      setSteps((prev) =>
        prev.map((s) =>
          s.id === stepId ? { ...s, data: { ...s.data, tagIds } } : s
        )
      );
    },
    []
  );

  const handleBeehiivAutomationIdsChange = useCallback(
    ({ stepId, automationIds }: { stepId: string; automationIds: string[] }) => {
      setSteps((prev) =>
        prev.map((s) =>
          s.id === stepId ? { ...s, data: { ...s.data, automationIds } } : s
        )
      );
    },
    []
  );

  const handleMetaAddToAudienceChange = useCallback(
    ({
      stepId,
      businessId,
      adAccountId,
      audienceId,
    }: {
      stepId: string;
      businessId?: string;
      adAccountId?: string;
      audienceId?: string;
    }) => {
      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== stepId) return s;
          const nextData = { ...s.data };
          if (businessId !== undefined) nextData.businessId = businessId;
          if (adAccountId !== undefined) nextData.adAccountId = adAccountId;
          if (audienceId !== undefined) nextData.audienceId = audienceId;
          return { ...s, data: nextData };
        })
      );
    },
    []
  );

  const handlePageStepChange = useCallback(
    ({
      stepId,
      pageId,
      formTargetStepIds,
    }: {
      stepId: string;
      pageId?: string;
      formTargetStepIds?: Record<string, string>;
    }) => {
      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== stepId) return s;
          const nextData = { ...s.data };
          if (pageId !== undefined) nextData.pageId = pageId;
          if (formTargetStepIds !== undefined) nextData.formTargetStepIds = formTargetStepIds;
          return { ...s, data: nextData };
        })
      );
    },
    []
  );

  const handleCheckoutStepChange = useCallback(
    ({
      stepId,
      lineItems,
      onCheckoutSuccess,
      checkoutSettings,
    }: {
      stepId: string;
      lineItems?: PageCheckoutLineItem[];
      onCheckoutSuccess?: PageOnCheckoutSuccessAction[];
      checkoutSettings?: PageCheckoutSettings;
    }) => {
      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== stepId) return s;
          return {
            ...s,
            data: {
              ...s.data,
              lineItems,
              onCheckoutSuccess,
              checkoutSettings,
            },
          };
        })
      );
    },
    []
  );

  const handleEditEmailBody = useCallback((stepId: string) => {
    const step = steps.find((s) => s.id === stepId);
    setEmailBodyEditValue(step?.data?.body ?? "");
    setEmailBodyEditStepId(stepId);
  }, [steps]);

  const handleSaveEmailBody = useCallback(() => {
    if (emailBodyEditStepId) {
      handleStepDataChange({ stepId: emailBodyEditStepId, field: "body", value: emailBodyEditValue });
      setEmailBodyEditStepId(null);
    }
  }, [emailBodyEditStepId, emailBodyEditValue, handleStepDataChange]);

  const handleDeleteStep = useCallback((stepId: string) => {
    setSteps((prev) => {
      const step = prev.find((s) => s.id === stepId);
      const filtered = prev.filter((s) => s.id !== stepId);
      const clearRefs = (steps: AutomationStep[]) =>
        steps.map((s) => {
          const formTargetStepIds = { ...(s.data?.formTargetStepIds ?? {}) };
          for (const [formId, targetStepId] of Object.entries(formTargetStepIds)) {
            if (targetStepId === stepId) {
              delete formTargetStepIds[formId];
            }
          }
          return {
            ...s,
            next: s.next === stepId ? undefined : s.next,
            data: {
              ...s.data,
              formTargetStepIds,
              buttons: (s.data?.buttons ?? []).map((b) => ({
                ...b,
                targetStepId: b.targetStepId === stepId ? undefined : b.targetStepId,
              })),
            },
          };
        });
      if (trigger?.next === stepId) {
        setTrigger((t) => (t ? { ...t, next: step?.next } : t));
      }
      return clearRefs(filtered);
    });
  }, [trigger?.next]);

  const handleDeleteThenEdge = useCallback(({ source, target }: { source: string; target: string }) => {
    setSelectedEdgeId(null);
    if (source === "trigger") {
      setTrigger((t) => (t ? { ...t, next: undefined } : t));
      return;
    }
    setSteps((prev) =>
      prev.map((s) => (s.id === source ? { ...s, next: s.next === target ? undefined : s.next } : s))
    );
  }, []);

  const handleConnect = useCallback((connection: Connection) => {
    const sourceId = connection.source;
    const targetId = connection.target;
    if (!sourceId || !targetId || sourceId === targetId) return;

    if (sourceId === "trigger") {
      if (steps.some((s) => s.id === targetId)) {
        setTrigger((t) => (t ? { ...t, next: targetId } : t));
      }
      return;
    }

    if (connection.sourceHandle?.startsWith("button-")) {
      const btnIdx = parseInt(connection.sourceHandle.replace("button-", ""), 10);
      if (isNaN(btnIdx)) return;

      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== sourceId) return s;
          const buttons = [...(s.data?.buttons ?? [])];
          buttons[btnIdx] = { text: buttons[btnIdx]?.text ?? "", targetStepId: targetId };
          return { ...s, data: { ...s.data, buttons } };
        })
      );
      return;
    }

    const sourceStep = steps.find((s) => s.id === sourceId);
    if (sourceStep?.type === "page" && connection.sourceHandle) {
      const formHtmlId = connection.sourceHandle;
      setSteps((prev) =>
        prev.map((s) => {
          if (s.id !== sourceId) return s;
          return {
            ...s,
            data: {
              ...s.data,
              formTargetStepIds: {
                ...(s.data?.formTargetStepIds ?? {}),
                [formHtmlId]: targetId,
              },
            },
          };
        })
      );
      return;
    }

    setSteps((prev) =>
      prev.map((s) => (s.id === sourceId ? { ...s, next: targetId } : s))
    );
  }, [steps]);

  const handleDeleteButtonConnection = useCallback(({ stepId, buttonIndex }: { stepId: string; buttonIndex: number }) => {
    setSelectedEdgeId(null);
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== stepId) return s;
        const buttons = [...(s.data?.buttons ?? [])];
        buttons[buttonIndex] = { text: buttons[buttonIndex]?.text ?? "", targetStepId: undefined };
        return { ...s, data: { ...s.data, buttons } };
      })
    );
  }, []);

  const handleDeleteFormConnection = useCallback(({ stepId, formHtmlId }: { stepId: string; formHtmlId: string }) => {
    setSelectedEdgeId(null);
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== stepId) return s;
        const formTargetStepIds = { ...(s.data?.formTargetStepIds ?? {}) };
        delete formTargetStepIds[formHtmlId];
        return { ...s, data: { ...s.data, formTargetStepIds } };
      })
    );
  }, []);

  const isValidConnection = useCallback(
    (connection: Connection) => {
      if (!connection.target || connection.source === connection.target) return false;
      const targetValid = steps.some((s) => s.id === connection.target);
      if (connection.source === "trigger") return targetValid;
      if (!targetValid) return false;
      if (connection.sourceHandle?.startsWith("button-")) return true;
      const sourceStep = steps.find((s) => s.id === connection.source);
      if (sourceStep?.type === "page" && connection.sourceHandle) return true;
      return steps.some((s) => s.id === connection.source);
    },
    [steps]
  );

  const handleAddTrigger = () => {
    setTrigger({ type: "contacts.create" });
    setIsStepDialogOpen(false);
  };

  const handleAddStep = (type: AutomationStep["type"]) => {
    const newId = generateStepId();
    const newStep: AutomationStep = {
      id: newId,
      type,
      data:
        type === "wait"
          ? { mode: "duration", unit: "minutes", timezone: artistTimezone }
          : type === "mailerlite.createSubscriber"
            ? { groupIds: [] }
            : type === "laylo.createSubscriber"
              ? { contactType: "phone" }
              : type === "manychat.createSubscriber"
                ? { tagIds: [] }
                : type === "beehiiv.createSubscriber"
                  ? { automationIds: [] }
                  : type === "meta.addToAudience"
                    ? { businessId: "", adAccountId: "", audienceId: "" }
                    : type === "page"
                      ? { pageId: "", formTargetStepIds: {} }
                      : type === "checkout"
                        ? { lineItems: [] }
                        : {},
    };
    setSteps((prev) => [...prev, newStep]);
    setIsStepDialogOpen(false);
  };

  const NODE_GAP = 64;
  const TRIGGER_WIDTH = 320;
  const STEP_WIDTH = 280;

  useEffect(() => {
    setNodes((currentNodes) => {
      const basePositions: { id: string; x: number; y: number }[] = [];
      if (trigger) {
        basePositions.push({ id: "trigger", x: 0, y: 0 });
      }
      let x = trigger ? TRIGGER_WIDTH + NODE_GAP : 0;
      steps.forEach((step) => {
        basePositions.push({ id: step.id, x, y: 0 });
        x += STEP_WIDTH + NODE_GAP;
      });

      let rightmostX = 0;
      let rightmostY = 0;
      for (const n of currentNodes) {
        const w = n.id === "trigger" ? TRIGGER_WIDTH : STEP_WIDTH;
        const right = n.position.x + w;
        if (right > rightmostX) {
          rightmostX = right;
          rightmostY = n.position.y;
        }
      }

      return basePositions.map(({ id, x: baseX, y: baseY }) => {
        const existingNode = currentNodes.find((n) => n.id === id);
        const saved =
          existingNode?.position ??
          (id === "trigger" ? trigger?.position : steps.find((s) => s.id === id)?.position);
        const isNewStep = id !== "trigger" && !existingNode && currentNodes.length > 0;
        const position = saved ?? (isNewStep ? { x: rightmostX + NODE_GAP, y: rightmostY } : { x: baseX, y: baseY });
        const isTrigger = id === "trigger";
        return {
          id,
          type: (isTrigger ? "trigger" : "step") as "trigger" | "step",
          position,
          data: isTrigger
            ? { trigger, onTriggerChange: handleTriggerChange, onDeleteTrigger: handleDeleteTrigger }
            : {
                step: steps.find((s) => s.id === id)!,
                onStepDataChange: handleStepDataChange,
                onStepButtonsChange: handleStepButtonsChange,
                onMailerLiteGroupIdsChange: handleMailerLiteGroupIdsChange,
                onManychatTagIdsChange: handleManychatTagIdsChange,
                onBeehiivAutomationIdsChange: handleBeehiivAutomationIdsChange,
                onMetaAddToAudienceChange: handleMetaAddToAudienceChange,
                onPageStepChange: handlePageStepChange,
                onCheckoutStepChange: handleCheckoutStepChange,
                onDeleteStep: handleDeleteStep,
                onEditEmailBody: handleEditEmailBody,
                artistTimezone,
              },
          draggable: true,
        } satisfies Node;
      });
    });

    setEdges(() => {
      const result: Edge[] = [];

      const triggerNext = trigger?.next;
      if (trigger && triggerNext && steps.some((s) => s.id === triggerNext)) {
        result.push({
          id: "trigger-to-" + triggerNext,
          source: "trigger",
          target: triggerNext,
          type: "then",
          selected: selectedEdgeId === "trigger-to-" + triggerNext,
          data: {
            hideLabel: true,
            onDelete: () => handleDeleteThenEdge({ source: "trigger", target: triggerNext }),
          },
        });
      }

      steps.forEach((step) => {
        const isSendMessage = step.type === "instagram.sendMessage";
        const isPage = step.type === "page";
        const isCheckout = step.type === "checkout";
        const buttons = step.data?.buttons ?? [];
        const hasButtons = isSendMessage && buttons.length > 0;
        const formTargetStepIds = step.data?.formTargetStepIds ?? {};

        if (!isPage && !isCheckout && step.next && steps.some((s) => s.id === step.next)) {
          result.push({
            id: `${step.id}-then-to-${step.next}`,
            source: step.id,
            sourceHandle: hasButtons ? "then" : undefined,
            target: step.next,
            type: "then",
            selected: selectedEdgeId === `${step.id}-then-to-${step.next}`,
            data: {
              hideLabel: true,
              onDelete: () => handleDeleteThenEdge({ source: step.id, target: step.next! }),
            },
          });
        }

        buttons.forEach((btn, bi) => {
          if (btn.targetStepId && steps.some((s) => s.id === btn.targetStepId)) {
            result.push({
              id: `${step.id}-button-${bi}-to-${btn.targetStepId}`,
              source: step.id,
              sourceHandle: `button-${bi}`,
              target: btn.targetStepId,
              type: "then",
              selected: selectedEdgeId === `${step.id}-button-${bi}-to-${btn.targetStepId}`,
              data: {
                label: btn.text || `Button ${bi + 1}`,
                onDelete: () => handleDeleteButtonConnection({ stepId: step.id, buttonIndex: bi }),
              },
            });
          }
        });

        Object.entries(formTargetStepIds).forEach(([formHtmlId, targetStepId]) => {
          if (targetStepId && steps.some((s) => s.id === targetStepId)) {
            result.push({
              id: `${step.id}-form-${formHtmlId}-to-${targetStepId}`,
              source: step.id,
              sourceHandle: formHtmlId,
              target: targetStepId,
              type: "then",
              selected: selectedEdgeId === `${step.id}-form-${formHtmlId}-to-${targetStepId}`,
              data: {
                label: formHtmlId,
                onDelete: () => handleDeleteFormConnection({ stepId: step.id, formHtmlId }),
              },
            });
          }
        });
      });

      return result;
    });
  }, [
    steps,
    trigger,
    selectedEdgeId,
    artistTimezone,
    handleTriggerChange,
    handleDeleteTrigger,
    handleStepDataChange,
    handleStepButtonsChange,
    handleMailerLiteGroupIdsChange,
    handleManychatTagIdsChange,
    handleBeehiivAutomationIdsChange,
    handleMetaAddToAudienceChange,
    handlePageStepChange,
    handleCheckoutStepChange,
    handleDeleteStep,
    handleEditEmailBody,
    handleDeleteButtonConnection,
    handleDeleteFormConnection,
    handleDeleteThenEdge,
  ]);

  const defaultViewport = useMemo(() => ({ x: 40, y: 80, zoom: 1 }), []);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (changes.some((c) => c.type === "position" && c.dragging)) {
        setPositionsDirty(true);
      }
      onNodesChange(changes);
    },
    [onNodesChange]
  );

  const hasUnsavedChanges =
    getFlowKey({ name, trigger, steps }) !== savedFlowKey || positionsDirty;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (hasUnsavedChanges && !isSaving && name.trim()) {
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasUnsavedChanges, isSaving, name, handleSave]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div>Flow not found</div>
      </div>
    );
  }

  const hasTemplate = Boolean(automation.template?.type);
  const tabFromUrl = searchParams.get("tab");
  const activeTab = tabFromUrl === "template" && hasTemplate ? "template" : "flow";
  const setActiveTab = (tab: "flow" | "template") => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="border-b border-gray-200 px-4 py-1.5 flex items-center gap-3">
        <Button type="button" variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div
          ref={nameRef}
          contentEditable
          onInput={handleNameChange}
          className="flex-1 outline-none text-sm font-medium min-w-0 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
          style={{ minHeight: "1.25rem" }}
          data-placeholder="Flow name"
        />
        {hasUnsavedChanges && (
          <Button
            type="button"
            size="icon"
            data-testid="automation-save"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="h-8 w-8"
            title="Save"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>
      {hasTemplate ? (
        <div className="flex border-b border-gray-200 shrink-0 overflow-x-auto min-w-0">
          <button
            type="button"
            onClick={() => setActiveTab("template")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              activeTab === "template"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Template
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("flow")}
            className={`shrink-0 px-4 py-2 text-sm font-medium ${
              activeTab === "flow"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Flow
          </button>
        </div>
      ) : null}
      {activeTab === "template" && automation.template ? (
        <TemplateTab
          automationId={id!}
          template={automation.template}
          formKey={automation.updatedAt}
          onRefreshed={applyLoadedAutomation}
        />
      ) : (
        <div className="flex-1 min-h-0 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          defaultEdgeOptions={{ zIndex: 1 }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultViewport={defaultViewport}
          nodesDraggable
          onConnect={handleConnect}
          onEdgeClick={(_, edge) => setSelectedEdgeId(edge.id)}
          onPaneClick={() => setSelectedEdgeId(null)}
          onNodeClick={() => setSelectedEdgeId(null)}
          isValidConnection={isValidConnection}
          zoomOnScroll
          fitView={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e2e8f0" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
        <Button
          type="button"
          size="icon"
          data-testid="automation-add-step"
          onClick={() => setIsStepDialogOpen(true)}
          className="absolute bottom-6 right-6 h-12 w-12 rounded-full shadow-lg z-10"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </div>
        )}
      <Dialog open={isStepDialogOpen} onOpenChange={setIsStepDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="sr-only">Add step</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4 max-h-[min(70vh,520px)] overflow-y-auto pr-1">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Engram</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="aspect-square w-full min-w-0">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={trigger != null}
                    title={trigger != null ? "A trigger already exists on this flow" : undefined}
                    data-testid="automation-add-trigger"
                    onClick={handleAddTrigger}
                    className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                  >
                    <Zap className="h-6 w-6" />
                    <span className="font-semibold text-sm">Trigger</span>
                  </Button>
                </div>
                {STEP_OPTIONS_MAIN.map((opt) => {
                  const isSmsDisabled = opt.value === "sms" && !hasTwilio;
                  const isSendMessageDisabled = opt.value === "instagram.sendMessage" && !hasMetaAccount;
                  const isDisabled = isSmsDisabled || isSendMessageDisabled;
                  const disabledTitle = isSmsDisabled
                    ? "Connect Twilio in Settings to use SMS"
                    : isSendMessageDisabled
                      ? "Connect Instagram in Settings to use Send Message"
                      : undefined;
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isDisabled}
                        title={disabledTitle}
                        data-testid={`automation-add-step-${opt.value}`}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">MailerLite</p>
              <div className="grid grid-cols-3 gap-3">
                {STEP_OPTIONS_MAILERLITE.map((opt) => {
                  const isMlDisabled = hasMailerLite !== true;
                  const disabledTitle =
                    hasMailerLite === null
                      ? "Loading MailerLite connection status…"
                      : "Connect MailerLite in Settings to use this step";
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isMlDisabled}
                        title={isMlDisabled ? disabledTitle : undefined}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Laylo</p>
              <div className="grid grid-cols-3 gap-3">
                {STEP_OPTIONS_LAYLO.map((opt) => {
                  const isLayloDisabled = hasLaylo !== true;
                  const disabledTitle =
                    hasLaylo === null
                      ? "Loading Laylo connection status…"
                      : "Connect Laylo in Settings to use this step";
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isLayloDisabled}
                        title={isLayloDisabled ? disabledTitle : undefined}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Manychat</p>
              <div className="grid grid-cols-3 gap-3">
                {STEP_OPTIONS_MANYCHAT.map((opt) => {
                  const isManychatDisabled = hasManychat !== true;
                  const disabledTitle =
                    hasManychat === null
                      ? "Loading Manychat connection status…"
                      : "Connect Manychat in Settings to use this step";
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isManychatDisabled}
                        title={isManychatDisabled ? disabledTitle : undefined}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Beehiiv</p>
              <div className="grid grid-cols-3 gap-3">
                {STEP_OPTIONS_BEEHIIV.map((opt) => {
                  const isBeehiivDisabled = hasBeehiiv !== true;
                  const disabledTitle =
                    hasBeehiiv === null
                      ? "Loading Beehiiv connection status…"
                      : "Connect Beehiiv in Settings to use this step";
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isBeehiivDisabled}
                        title={isBeehiivDisabled ? disabledTitle : undefined}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Meta</p>
              <div className="grid grid-cols-3 gap-3">
                {STEP_OPTIONS_META.map((opt) => {
                  const isMetaDisabled = hasMetaFacebook !== true;
                  const disabledTitle =
                    hasMetaFacebook === null
                      ? "Loading Meta connection status…"
                      : "Connect Meta in Settings to use this step";
                  return (
                    <div key={opt.value} className="aspect-square w-full min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isMetaDisabled}
                        title={isMetaDisabled ? disabledTitle : undefined}
                        onClick={() => handleAddStep(opt.value)}
                        className="w-full h-full flex flex-col gap-2 p-4 bg-background"
                      >
                        {opt.icon}
                        <span className="font-semibold text-sm">{opt.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsStepDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={emailBodyEditStepId != null} onOpenChange={(open) => !open && setEmailBodyEditStepId(null)}>
        <DialogContent className="fixed inset-0 z-50 w-screen h-screen max-w-none translate-x-0 translate-y-0 rounded-none flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle>Edit Email Body (HTML)</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden flex min-w-0">
            <Tabs
              defaultValue="code"
              className="flex-1 flex flex-col min-h-0 overflow-hidden min-w-0"
            >
              <div className="border-b px-4 py-2 shrink-0">
                <TabsList>
                  <TabsTrigger value="code">Code</TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="code"
                className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <CodeMirror
                  value={emailBodyEditValue}
                  height="100%"
                  extensions={[htmlLang(), EditorView.lineWrapping]}
                  onChange={setEmailBodyEditValue}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    autocompletion: true,
                  }}
                  className="h-full text-sm"
                />
              </TabsContent>
              <TabsContent
                value="preview"
                className="flex-1 min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <iframe
                  title="Email preview"
                  srcDoc={emailBodyEditValue || "<div></div>"}
                  className="w-full h-full border-0 bg-white"
                  sandbox=""
                />
              </TabsContent>
            </Tabs>
            <PageHtmlChatPanel
              html={emailBodyEditValue}
              onHtmlChange={setEmailBodyEditValue}
              onSave={(savedHtml) => {
                if (savedHtml !== undefined) {
                  setEmailBodyEditValue(savedHtml);
                }
              }}
            />
          </div>
          <DialogFooter className="p-4 border-t shrink-0">
            <Button type="button" variant="outline" onClick={() => setEmailBodyEditStepId(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveEmailBody}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
